// frontend/src/components/ForumPage.jsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import ForumReplies from "./ForumReplies";
import apiClient from "../Api/apiConfig";
import { Link } from "react-router-dom";
import { ThumbsUp, MessageCircle, Share2, Filter } from "lucide-react";
import "../styles/ForumPage.css";

export default function ForumPage() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  const [landlords, setLandlords] = useState([]);
  const [themes, setThemes] = useState([]);
  const [activeLandlord, setActiveLandlord] = useState(null);
  const [activeTheme, setActiveTheme] = useState(null);
  const [posts, setPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [newPost, setNewPost] = useState({ title: "", content: "" });
  const [replyContents, setReplyContents] = useState({});

  // ✅ utilitaire pour formater la date façon "Facebook"
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("fr-FR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  };

  // ✅ initiales pour l’avatar rond
  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Redirection si l'utilisateur n'est pas connecté
  useEffect(() => {
    if (!isAuthenticated) {
      alert("Vous devez être connecté pour accéder à cette page.");
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  // Récupération des bailleurs
  useEffect(() => {
    const fetchLandlords = async () => {
      try {
        const response = await apiClient.get("/api/landlords");
        if (Array.isArray(response.data)) {
          setLandlords(response.data);
        } else {
          console.error("Les données des bailleurs ne sont pas un tableau :", response.data);
          setLandlords([]);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des bailleurs :", error.message);
        setLandlords([]);
      }
    };
    fetchLandlords();
  }, []);

  // Récupération des thèmes
  useEffect(() => {
    const fetchThemes = async () => {
      try {
        const response = await apiClient.get("/api/themes");
        if (Array.isArray(response.data)) {
          setThemes(response.data);
        } else {
          console.error("Les données des thèmes ne sont pas un tableau :", response.data);
          setThemes([]);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des thèmes :", error.message);
        setThemes([]);
      }
    };
    fetchThemes();
  }, []);

  // Récupération des posts
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await apiClient.get("/api/forum-posts");

        if (!response.data || !Array.isArray(response.data)) {
          console.error("Les données des posts ne sont pas valides :", response.data);
          setPosts([]);
          setFilteredPosts([]);
          return;
        }

        const enrichedPosts = response.data.map((post) => {
          const landlord = Array.isArray(landlords)
            ? landlords.find((l) => l.id === post.landlord_id)
            : null;
          const theme = Array.isArray(themes)
            ? themes.find((t) => t.id === post.theme_id)
            : null;

          return {
            ...post,
            landlordName: landlord ? landlord.name : "Bailleur inconnu",
            themeName: theme ? theme.name : "Thème inconnu",
          };
        });

        const sortedPosts = enrichedPosts.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        setPosts(sortedPosts);
        setFilteredPosts(sortedPosts);
      } catch (error) {
        console.error("Erreur lors du chargement des posts :", error.message);
        setPosts([]);
        setFilteredPosts([]);
      }
    };

    if (landlords.length > 0 && themes.length > 0) {
      fetchPosts();
    }
    if ((landlords.length > 0 || themes.length > 0) && posts.length === 0) {
      fetchPosts();
    }
  }, [landlords, themes, posts.length]);

  // Filtrage des posts en mode "fil d’actus"
  useEffect(() => {
    let filtered = Array.isArray(posts) ? posts : [];

    if (activeLandlord) {
      filtered = filtered.filter((post) => post.landlord_id === activeLandlord.id);
    }

    if (activeTheme) {
      filtered = filtered.filter((post) => post.theme_id === activeTheme.id);
    }

    setFilteredPosts(filtered);
  }, [activeLandlord, activeTheme, posts]);

  // Soumettre un nouveau post
  const handleSubmitPost = async (e) => {
    e.preventDefault();

    if (!newPost.title.trim() || !newPost.content.trim()) {
      alert("Veuillez remplir tous les champs du post.");
      return;
    }

    if (!activeTheme || !activeLandlord) {
      alert("Veuillez sélectionner un bailleur et un thème pour publier un nouveau post.");
      return;
    }

    try {
      const response = await apiClient.post("/api/forum-posts", {
        ...newPost,
        theme_id: activeTheme.id,
        landlord_id: activeLandlord.id,
        author_id: user.id,
      });

      const newPostWithNames = {
        ...response.data,
        landlordName: activeLandlord.name,
        themeName: activeTheme.name,
      };

      setPosts((prev) => [newPostWithNames, ...prev]);
      setNewPost({ title: "", content: "" });
    } catch (error) {
      console.error("Erreur lors de la création du post :", error.message);
      alert("Une erreur s'est produite lors de la création du post.");
    }
  };

  // Soumettre une réponse
  const handleSubmitReply = async (postId) => {
    const content = replyContents[postId]?.trim();

    if (!content) {
      alert("Le contenu de la réponse est vide.");
      return;
    }

    try {
      const response = await apiClient.post(`/api/forum-posts/${postId}/replies`, {
        content,
        author_id: user.id,
      });

      setPosts((prev) =>
        prev.map((post) =>
          post.id === postId
            ? {
                ...post,
                replies: Array.isArray(post.replies)
                  ? [...post.replies, response.data]
                  : [response.data],
              }
            : post
        )
      );

      setReplyContents((prev) => ({ ...prev, [postId]: "" }));
    } catch (error) {
      console.error("Erreur lors de la création de la réponse :", error.message);
      alert("Une erreur s'est produite lors de la réponse.");
    }
  };

  return (
    <div className="forum-page fb-layout">
      {/* COLONNE GAUCHE – filtres façon menu Facebook */}
      <aside className="fb-sidebar-left">
        <div className="fb-sidebar-card">
          <div className="fb-sidebar-title">
            <Filter size={16} />
            <span>Filtres</span>
          </div>

          <div className="fb-sidebar-section">
            <h3>Bailleurs</h3>
            <button
              className={`fb-filter-btn ${!activeLandlord ? "active" : ""}`}
              onClick={() => {
                setActiveLandlord(null);
                setActiveTheme(null);
              }}
            >
              Tous les posts
            </button>
            {Array.isArray(landlords) &&
              landlords.map((landlord) => (
                <button
                  key={landlord.id}
                  className={`fb-filter-btn ${
                    activeLandlord?.id === landlord.id ? "active" : ""
                  }`}
                  onClick={() => {
                    setActiveLandlord(landlord);
                    // on garde le thème sélectionné, comme un filtre combiné
                  }}
                >
                  {landlord.name}
                </button>
              ))}
          </div>

          <div className="fb-sidebar-section">
            <h3>Thèmes</h3>
            {Array.isArray(themes) &&
              themes.map((theme) => (
                <button
                  key={theme.id}
                  className={`fb-filter-btn ${
                    activeTheme?.id === theme.id ? "active" : ""
                  }`}
                  onClick={() => setActiveTheme(theme)}
                >
                  {theme.name}
                </button>
              ))}
          </div>
        </div>
      </aside>

      {/* COLONNE CENTRALE – fil d’actualités */}
      <main className="fb-main-feed">
        {/* Bandeau en haut */}
        <header className="fb-header-bar">
          <h2>Fil d’actualités des locataires</h2>
          <p className="fb-header-sub">
            {activeLandlord ? `Bailleur : ${activeLandlord.name}` : "Tous les bailleurs"}
            {activeTheme ? ` · Thème : ${activeTheme.name}` : ""}
          </p>
        </header>

        {/* Composer "Quoi de neuf ?" */}
        <section className="fb-composer-card">
          <div className="fb-composer-header">
            <div className="fb-avatar">
              {getInitials(user?.name || user?.email || "Vous")}
            </div>
            <div className="fb-composer-title">
              {activeLandlord && activeTheme ? (
                <span>
                  {`Quoi de neuf ? (`}
                  <strong>{activeLandlord.name}</strong>
                  {" · "}
                  <strong>{activeTheme.name}</strong>
                  {")"}
                </span>
              ) : (
                <span>
                  Sélectionnez d’abord un <strong>bailleur</strong> et un <strong>thème</strong> à
                  gauche pour publier.
                </span>
              )}
            </div>
          </div>

          <form onSubmit={handleSubmitPost} className="fb-composer-form">
            <input
              type="text"
              className="fb-input"
              placeholder="Titre de votre publication"
              value={newPost.title}
              onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
            />

            <textarea
              className="fb-textarea"
              placeholder="Exprimez-vous…"
              rows={3}
              value={newPost.content}
              onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
            />

            <div className="fb-composer-footer">
              <span className="fb-composer-hint">
                Votre message sera visible dans le fil public des locataires.
              </span>
              <button
                type="submit"
                className="fb-primary-btn"
                disabled={!activeLandlord || !activeTheme}
              >
                Publier
              </button>
            </div>
          </form>
        </section>

        {/* Liste des posts = fil d’actus */}
        <section className="fb-posts-list">
          {Array.isArray(filteredPosts) && filteredPosts.length > 0 ? (
            filteredPosts.map((post) => (
              <article key={post.id} className="fb-post-card">
                <div className="fb-post-header">
                  <div className="fb-avatar">
                    {getInitials(post.landlordName || "B")}
                  </div>
                  <div className="fb-post-header-info">
                    <div className="fb-post-author">
                      {post.landlordName || "Bailleur inconnu"}
                    </div>
                    <div className="fb-post-meta">
                      <span>{post.themeName || "Thème inconnu"}</span>
                      {post.created_at && (
                        <>
                          <span className="fb-dot">•</span>
                          <span>{formatDate(post.created_at)}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="fb-post-body">
                  <h3 className="fb-post-title">{post.title}</h3>
                  <p className="fb-post-content">{post.content}</p>
                </div>

                <div className="fb-post-actions">
                  <button className="fb-action-btn" type="button">
                    <ThumbsUp size={16} />
                    <span>J’aime</span>
                  </button>
                  <button className="fb-action-btn" type="button">
                    <MessageCircle size={16} />
                    <span>Commenter</span>
                  </button>
                  <button className="fb-action-btn" type="button">
                    <Share2 size={16} />
                    <span>Partager</span>
                  </button>
                </div>

                <div className="fb-post-comments">
                  {/* anciennes réponses */}
                  <ForumReplies postId={post.id} />

                  {/* nouvelle réponse */}
                  <div className="fb-reply-composer">
                    <div className="fb-avatar small">
                      {getInitials(user?.name || user?.email || "Vous")}
                    </div>
                    <div className="fb-reply-input-wrapper">
                      <textarea
                        className="fb-reply-textarea"
                        placeholder="Écrire un commentaire…"
                        rows={1}
                        value={replyContents[post.id] || ""}
                        onChange={(e) =>
                          setReplyContents((prev) => ({
                            ...prev,
                            [post.id]: e.target.value,
                          }))
                        }
                      />
                      <button
                        className="fb-secondary-btn"
                        type="button"
                        onClick={() => handleSubmitReply(post.id)}
                      >
                        Publier
                      </button>
                    </div>
                  </div>
                </div>
              </article>
            ))
          ) : (
            <div className="fb-empty-state">
              <p>Aucune publication ne correspond à ces filtres.</p>
              <p className="fb-empty-sub">
                Choisissez un autre bailleur / thème ou créez le premier post !
              </p>
            </div>
          )}
        </section>
      </main>

      {/* COLONNE DROITE – mini infos (facultatif) */}
      <aside className="fb-sidebar-right">
        <div className="fb-sidebar-card">
          <h3>Infos forum</h3>
          <p>
            Cet espace permet aux locataires d’échanger librement autour de leurs bailleurs et
            des différents thèmes (charges, travaux, ascenseurs, etc.).
          </p>
          <p className="fb-info-small">
            Restez courtois, pas de noms propres ni d’attaques personnelles.
          </p>
        </div>
      </aside>
    </div>
  );
}
